import type { DropdownMenuItem } from '@nuxt/ui';
import type { ComputedRef } from 'vue';
import { useOverlay } from '@nuxt/ui/composables';
import { storeToRefs } from 'pinia';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from '#app/composables/router';
import { useSessionEnd } from '#layers/access/public';
import { featureFlagsVisible } from '#layers/base/public';
import { useSettingsOverlay } from '#layers/global-settings/public';
import { useWorkspaceStore } from '#layers/workspace/public';

const LazyFeatureFlagModal = defineAsyncComponent(() => import('~/components/FeatureFlag/FeatureFlagModal.vue'));

export function useUserMenuItems(): ComputedRef<DropdownMenuItem[]> {
	const { t } = useI18n();
	const router = useRouter();
	const { logoutAndReload } = useSessionEnd();
	const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());

	const overlay = useOverlay();
	const { open } = useSettingsOverlay();

	async function logout() {
		await logoutAndReload(router.resolve({ name: 'auth.login' }).fullPath);
	}

	return computed(() => {
		const list: DropdownMenuItem[] = [];

		if (currentWorkspaceSlug.value) {
			list.push({
				label: t('organization.dashboard.user_menu.account_settings'),
				onSelect: () => open('account/profile'),
			});
		}

		if (featureFlagsVisible.value) {
			list.push({
				label: 'Feature Flags',
				onSelect() {
					overlay.create(LazyFeatureFlagModal, {
						destroyOnClose: true,
					}).open();
				},
			});
		}

		list.push({
			label: t('account.logout'),
			onSelect: logout,
		});

		return list;
	});
}
